import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Calendar, ArrowRight, X, User,
  Phone, Mail, MapPin, CheckCircle, AlertCircle, Loader2, Sparkles, BookOpen
} from 'lucide-react';
import { submitCourseRegistration } from '../services/contactService';

const WP_API_URL = 'https://dimensionias.com/cms/wp-json/wp/v2';

// Helper to extract first img tag src from HTML string if featured image isn't set
const extractFirstImage = (htmlContent) => {
  if (!htmlContent) return null;
  const match = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
};

// Helper to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const CourseUpdatesSlider = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: ''
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: null });

  // Fetch posts from WordPress CMS
  useEffect(() => {
    const defaultCourses = [
      {
        id: 'd1',
        title: 'UPSC CSE Integrated Foundation Batch 2026-27',
        excerpt: 'Comprehensive 3-stage preparation (Prelims + Mains + Interview) with 1-on-1 personal mentorship and daily answer evaluation.',
        content: '<p>Our flagship <strong>UPSC Foundation Batch</strong> provides complete coverage of GS Papers I-IV, Essay, CSAT, and Current Affairs. Includes daily answer writing practice, weekly tests, and personal guidance by experienced faculty.</p><ul><li>Complete GS Prelims & Mains Syllabus</li><li>Daily Mains Answer Writing & Personal Evaluation</li><li>Comprehensive Printed Study Material</li><li>Dedicated One-on-One Mentorship</li></ul>',
        image: '/images/hero_img.png',
        date: new Date().toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
      },
      {
        id: 'd2',
        title: 'CAPF AC Intensive Mentorship & Test Series',
        excerpt: 'Specialized strategy & paper evaluation for Paper I & Paper II under guidance of CAPF AIR Rankers.',
        content: '<p>Prepare for <strong>CAPF Assistant Commandant</strong> with targeted guidance for Essay, Precis, Report Writing, and Paper I GS/Mental Ability. Features rigorous test series with detailed ranker feedback.</p>',
        image: '/images/hero_img.png',
        date: new Date().toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
      }
    ];

    const fetchCoursePosts = async () => {
      setLoading(true);
      try {
        // Fetch categories to find course updates category if it exists
        const catRes = await fetch(`${WP_API_URL}/categories?per_page=100`);
        let categoryId = null;
        if (catRes.ok) {
          const categories = await catRes.json();
          const courseCat = categories.find(cat =>
            cat.slug === 'course-updates' ||
            cat.slug === 'courses' ||
            cat.slug === 'announcements' ||
            cat.slug === 'updates' ||
            cat.name.toLowerCase().includes('course')
          );
          if (courseCat) categoryId = courseCat.id;
        }

        // Build endpoint
        let endpoint = `${WP_API_URL}/posts?per_page=10&_embed=true&status=publish`;
        if (categoryId) {
          endpoint += `&categories=${categoryId}`;
        }

        const res = await fetch(endpoint);
        if (res.ok) {
          const posts = await res.json();
          if (posts.length > 0) {
            const formatted = posts.map(post => {
              // Extract featured image from standard WP featured media or fallback body img
              const featuredMedia = post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
                post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.large?.source_url ||
                extractFirstImage(post.content?.rendered) ||
                '/images/hero_img.png';

              return {
                id: post.id,
                title: stripHtml(post.title?.rendered),
                excerpt: stripHtml(post.excerpt?.rendered || post.content?.rendered).slice(0, 140) + '...',
                content: post.content?.rendered || post.excerpt?.rendered || '',
                image: featuredMedia,
                date: new Date(post.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }),
                slug: post.slug
              };
            });

            setCourses(formatted);
          } else {
            setCourses(defaultCourses);
          }
        } else {
          setCourses(defaultCourses);
        }
      } catch (err) {
        console.error('Error fetching course updates from WordPress:', err);
        setCourses(defaultCourses);
      } finally {
        setLoading(false);
      }
    };

    fetchCoursePosts();
  }, []);

  // Auto slide interval (5 seconds)
  useEffect(() => {
    if (isHovered || courses.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % courses.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHovered, courses.length]);

  const handlePrev = () => {
    setCurrentIndex(prev => (prev - 1 + courses.length) % courses.length);
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % courses.length);
  };

  // Open detail modal
  const openCourseModal = (course) => {
    setSelectedCourse(course);
    setShowRegisterForm(false);
    setFormStatus({ loading: false, success: false, error: null });
    document.body.style.overflow = 'hidden';
  };

  // Close detail modal
  const closeCourseModal = () => {
    setSelectedCourse(null);
    setShowRegisterForm(false);
    setFormData({ name: '', contact: '', email: '', city: '' });
    setFormStatus({ loading: false, success: false, error: null });
    document.body.style.overflow = 'unset';
  };

  // Form submit handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact || !formData.email) {
      setFormStatus({ loading: false, success: false, error: 'Please fill in all required fields.' });
      return;
    }

    setFormStatus({ loading: true, success: false, error: null });

    const result = await submitCourseRegistration({
      name: formData.name,
      contact: formData.contact,
      email: formData.email,
      city: formData.city,
      course: selectedCourse?.title || 'Course Enquiry'
    });

    if (result.success) {
      setFormStatus({ loading: false, success: true, error: null });
    } else {
      setFormStatus({ loading: false, success: false, error: result.message || 'Submission failed.' });
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 py-6">
        <div className="w-full aspect-[16/9] max-h-[450px] bg-slate-100 rounded-3xl animate-pulse flex flex-col items-center justify-center gap-3 border border-slate-200">
          <Loader2 className="w-8 h-8 text-[#134E4A] animate-spin" />
          <span className="text-sm font-bold text-slate-500">Loading Latest Course Banners...</span>
        </div>
      </div>
    );
  }

  if (courses.length === 0) return null;

  const currentCourse = courses[currentIndex];

  return (
    <div className="w-full bg-gradient-to-b from-[#F0FDF7] to-white py-2 sm:py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header Tag */}


        {/* ---------------- 10:1 ULTRA-THIN STRIP IMAGE SLIDER ---------------- */}
        <div
          className="relative w-full aspect-[10/1] min-h-[90px] sm:min-h-[120px] max-h-[140px] rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-emerald-900/20 group cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => openCourseModal(currentCourse)}
        >
          {/* Main Course Banner Image */}
          <img
            src={currentCourse.image}
            alt={currentCourse.title}
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/hero_img.png';
            }}
          />

          {/* Gradient Overlay for Readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-transparent flex items-center justify-between px-4 sm:px-6 lg:px-8 text-white">

            {/* Content Left Overlay */}
            <div className="max-w-3xl min-w-0 pr-4">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="bg-[#134E4A]/90 text-white text-[9px] sm:text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-400/40 flex items-center gap-1">
                  <BookOpen className="w-2.5 h-2.5 text-[#2DD4BF]" />
                  Course Update
                </span>
                <span className="text-[10px] font-bold text-emerald-300 hidden sm:inline-flex items-center gap-1">
                  <Calendar className="w-2.5 h-2.5" />
                  {currentCourse.date}
                </span>
              </div>

              <h2 className="text-xs sm:text-base lg:text-lg font-black text-white leading-tight drop-shadow-md group-hover:text-[#2DD4BF] transition-colors truncate">
                {currentCourse.title}
              </h2>
            </div>

            {/* Right Action Button */}
            <div className="shrink-0 flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openCourseModal(currentCourse);
                }}
                className="bg-[#2DD4BF] text-[#134E4A] font-extrabold text-[10px] sm:text-xs px-3.5 py-1.5 sm:py-2 rounded-lg shadow hover:bg-white transition-all flex items-center gap-1 whitespace-nowrap transform group-hover:translate-x-1"
              >
                <span>View Details & Register</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Navigation Controls (Previous / Next) */}
          {courses.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-slate-950/60 hover:bg-[#134E4A] text-white flex items-center justify-center transition-all border border-white/20 backdrop-blur-md opacity-80 group-hover:opacity-100 shadow-xl"
                title="Previous Course"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-slate-950/60 hover:bg-[#134E4A] text-white flex items-center justify-center transition-all border border-white/20 backdrop-blur-md opacity-80 group-hover:opacity-100 shadow-xl"
                title="Next Course"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Bottom Dot Indicators */}
          {courses.length > 1 && (
            <div className="absolute bottom-3 right-4 sm:bottom-4 sm:right-6 flex items-center gap-1.5 z-10">
              {courses.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(idx);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === currentIndex
                    ? 'w-7 bg-[#2DD4BF]'
                    : 'w-2 bg-white/50 hover:bg-white'
                    }`}
                  title={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* ---------------- COURSE DETAILS & ENQUIRY MODAL ---------------- */}
      {selectedCourse && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeCourseModal}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden border border-slate-200 my-8 max-h-[90vh] flex flex-col relative animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeCourseModal}
              className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-slate-900/70 text-white hover:bg-slate-900 flex items-center justify-center transition-all border border-white/30"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header Banner Image (16:9) */}
            <div className="relative w-full aspect-[16/9] max-h-[300px] shrink-0 bg-slate-900 overflow-hidden">
              <img
                src={selectedCourse.image}
                alt={selectedCourse.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/hero_img.png';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent flex items-end p-6">
                <div>
                  <span className="bg-[#2DD4BF] text-[#134E4A] text-xs font-black px-3 py-1 rounded-full mb-2 inline-block">
                    Course Information
                  </span>
                  <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
                    {selectedCourse.title}
                  </h2>
                </div>
              </div>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-6 sm:p-8 overflow-y-auto flex-1 space-y-6">

              {/* Publication Date */}
              <div className="flex items-center gap-2 text-xs font-bold text-slate-500 border-b pb-3 border-slate-100">
                <Calendar className="w-4 h-4 text-[#134E4A]" />
                <span>Posted on: {selectedCourse.date}</span>
              </div>

              {/* Course Description HTML */}
              <div className="prose prose-slate max-w-none text-slate-700 text-sm leading-relaxed">
                {selectedCourse.content ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedCourse.content }} />
                ) : (
                  <p>{selectedCourse.excerpt}</p>
                )}
              </div>

              {/* Enrollment / Registration Area */}
              <div className="bg-emerald-50/80 border-2 border-emerald-200 rounded-2xl p-5 mt-6">
                {!showRegisterForm && !formStatus.success && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <h4 className="font-extrabold text-[#134E4A] text-base mb-1">
                        Interested in this Course?
                      </h4>
                      <p className="text-xs text-slate-600 font-medium">
                        Fill out a quick form to reserve your seat or talk with our mentor.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowRegisterForm(true)}
                      className="w-full sm:w-auto bg-[#134E4A] text-white font-extrabold text-xs sm:text-sm px-6 py-3 rounded-xl hover:bg-[#0F3D39] transition-all shadow-md shrink-0 flex items-center justify-center gap-2"
                    >
                      <span>Register / Enquire Now</span>
                      <ArrowRight className="w-4 h-4 text-[#2DD4BF]" />
                    </button>
                  </div>
                )}

                {/* Registration Form */}
                {showRegisterForm && !formStatus.success && (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="flex items-center justify-between border-b border-emerald-200/80 pb-2">
                      <h4 className="font-extrabold text-[#134E4A] text-sm">
                        Registration Form — {selectedCourse.title}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowRegisterForm(false)}
                        className="text-xs text-slate-500 hover:text-slate-800 font-bold"
                      >
                        Cancel
                      </button>
                    </div>

                    {formStatus.error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs font-bold">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{formStatus.error}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-slate-700 mb-1 block">
                          Full Name *
                        </label>
                        <div className="relative">
                          <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                          <input
                            type="text"
                            required
                            placeholder="Enter your name"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#134E4A] focus:outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 mb-1 block">
                          Contact Number *
                        </label>
                        <div className="relative">
                          <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                          <input
                            type="tel"
                            required
                            placeholder="10-digit mobile number"
                            value={formData.contact}
                            onChange={e => setFormData({ ...formData, contact: e.target.value })}
                            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#134E4A] focus:outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 mb-1 block">
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                          <input
                            type="email"
                            required
                            placeholder="your.email@example.com"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#134E4A] focus:outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-slate-700 mb-1 block">
                          City
                        </label>
                        <div className="relative">
                          <MapPin className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Your current city"
                            value={formData.city}
                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#134E4A] focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus.loading}
                      className="w-full bg-[#134E4A] text-white font-extrabold text-xs py-3 rounded-xl hover:bg-[#0F3D39] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {formStatus.loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <span>Submit Course Registration</span>
                      )}
                    </button>
                  </form>
                )}

                {/* Success Banner */}
                {formStatus.success && (
                  <div className="text-center py-4 space-y-2">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 text-[#134E4A] flex items-center justify-center mx-auto">
                      <CheckCircle className="w-7 h-7" />
                    </div>
                    <h4 className="font-extrabold text-[#134E4A] text-base">
                      Registration Submitted Successfully!
                    </h4>
                    <p className="text-xs text-slate-600">
                      Thank you, <strong>{formData.name}</strong>. Our team will contact you shortly regarding <strong>{selectedCourse.title}</strong>.
                    </p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseUpdatesSlider;
